import React, { useEffect, useState } from "react";
import { TopNav } from "@/components/TopNav";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { formatVND } from "@/lib/money";
import { ShoppingBag, Coins, Sparkles, Crown, Tag, Lock, Check, Star } from "lucide-react";
import { toast } from "sonner";

const TYPES = [
  { key: "avatar_frame", label: "Khung Avatar", icon: "🖼️" },
  { key: "rank_frame", label: "Khung Rank", icon: "👑" },
  { key: "title", label: "Danh hiệu", icon: "🏷️" },
  { key: "tv_effect", label: "Hiệu ứng TV", icon: "✨" },
];

const RARITY_STYLES = {
  common: { color: "#94a3b8", bg: "rgba(148,163,184,0.08)", border: "rgba(148,163,184,0.3)", label: "Thường" },
  rare: { color: "#38bdf8", bg: "rgba(56,189,248,0.08)", border: "rgba(56,189,248,0.4)", label: "Hiếm" },
  epic: { color: "#c084fc", bg: "rgba(192,132,252,0.08)", border: "rgba(192,132,252,0.4)", label: "Sử Thi" },
  legendary: { color: "#fbbf24", bg: "rgba(251,191,36,0.1)", border: "rgba(251,191,36,0.5)", label: "Huyền Thoại" },
};

const TV_EFFECT_PREVIEW = {
  neon: { color: "#22d3ee", textShadow: "0 0 8px #22d3ee, 0 0 16px #22d3ee" },
  fire: { color: "#fb923c", textShadow: "0 0 8px #ef4444, 0 0 18px #f97316" },
  gold: { color: "#fde047", textShadow: "0 0 8px #ca8a04, 0 0 16px #facc15" },
  rainbow: { background: "linear-gradient(90deg,#ef4444,#f59e0b,#84cc16,#06b6d4,#a855f7)", WebkitBackgroundClip: "text", color: "transparent" },
  electric: { color: "#a5f3fc", textShadow: "0 0 6px #06b6d4, 0 0 14px #38bdf8" },
};

function ItemPreview({ item, size = 56 }) {
  if (item.type === "avatar_frame") {
    return (
      <div className="rounded-2xl p-[3px]" style={{ background: item.asset, width: size, height: size }}>
        <div className="w-full h-full rounded-xl bg-[#0c0d14] grid place-items-center text-xl">{item.icon || "👤"}</div>
      </div>
    );
  }
  if (item.type === "rank_frame") {
    return (
      <div className="rounded-md px-3 py-2 font-display font-black text-sm" style={{ background: item.asset, color: "#0c0d14", minWidth: size }}>
        RANK
      </div>
    );
  }
  if (item.type === "title") {
    return (
      <div className="px-3 py-1.5 rounded-md text-sm font-display font-bold border border-amber-400/40 bg-amber-400/10 text-amber-300">
        {item.asset}
      </div>
    );
  }
  if (item.type === "tv_effect") {
    const st = TV_EFFECT_PREVIEW[item.asset] || {};
    return <div className="font-display font-black text-2xl" style={st}>NICKNAME</div>;
  }
  return null;
}

export default function Shop() {
  const { user, refresh } = useAuth();
  const [items, setItems] = useState([]);
  const [inv, setInv] = useState({ inventory: [], equipped: {}, reward_points: 0 });
  const [filter, setFilter] = useState("avatar_frame");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [itemsRes, invRes] = await Promise.all([api.get("/shop/items"), api.get("/me/inventory")]);
    setItems(itemsRes.data);
    setInv(invRes.data);
  };

  useEffect(() => { load(); }, []);

  const ownedIds = new Set(inv.inventory.map((i) => i.id));
  const equippedIds = new Set(Object.values(inv.equipped || {}).filter(Boolean));

  const buy = async (item, currency) => {
    if (busy) return;
    setBusy(true);
    try {
      await api.post("/shop/purchase", { item_id: item.id, currency });
      toast.success(`Đã mua: ${item.name}`);
      await load();
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Mua thất bại");
    } finally {
      setBusy(false);
    }
  };

  const equip = async (item) => {
    try {
      await api.post("/me/equip", { item_id: item.id, slot: item.type });
      toast.success(`Đã trang bị: ${item.name}`);
      await load();
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Không thể trang bị");
    }
  };

  const unequip = async (slot) => {
    try {
      await api.post("/me/equip", { item_id: null, slot });
      toast.success("Đã gỡ trang bị");
      await load();
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Lỗi");
    }
  };

  const filtered = items.filter((i) => i.type === filter);

  return (
    <div className="min-h-screen bg-[#05050a]">
      <TopNav />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 space-y-5">
        {/* Header / Wallet */}
        <div className="relative rounded-2xl overflow-hidden p-5 border border-amber-400/30" style={{ background: "linear-gradient(135deg,#1a1208 0%,#2a1c08 50%,#1a1208 100%)" }}>
          <div className="absolute -top-10 -right-10 w-48 h-48 rounded-full blur-3xl bg-amber-500/20" />
          <div className="relative flex items-center justify-between flex-wrap gap-3">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <ShoppingBag size={18} className="text-amber-300" />
                <h1 className="font-display font-black text-xl uppercase tracking-wider">Cửa hàng vật phẩm</h1>
              </div>
              <div className="text-xs text-amber-300/70">Mua khung, danh hiệu, hiệu ứng TV bằng VND hoặc điểm thưởng</div>
            </div>
            <div className="flex items-center gap-3 flex-wrap" data-testid="shop-balances">
              <div className="px-3 py-2 rounded-lg bg-black/30 border border-amber-400/30">
                <div className="text-[10px] uppercase tracking-wider text-amber-300/70">Ví VND</div>
                <div className="font-display font-bold text-amber-300 text-lg">{formatVND(user?.coin || 0)}</div>
              </div>
              <div className="px-3 py-2 rounded-lg bg-black/30 border border-cyan-400/30">
                <div className="text-[10px] uppercase tracking-wider text-cyan-300/70">Điểm thưởng</div>
                <div className="font-display font-bold text-cyan-300 text-lg flex items-center gap-1.5"><Star size={14} /> {inv.reward_points || 0}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Equipped panel */}
        <div className="rounded-2xl bg-[#0c0d14] border border-white/8 p-4">
          <div className="flex items-center gap-2 mb-3">
            <Crown size={14} className="text-amber-300" />
            <h2 className="font-display font-bold text-sm uppercase tracking-wider">Đang trang bị</h2>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="equipped-grid">
            {TYPES.map((t) => {
              const eqId = inv.equipped?.[t.key];
              const eqItem = inv.inventory.find((i) => i.id === eqId);
              return (
                <div key={t.key} className="rounded-xl bg-white/[0.03] border border-white/8 p-3 min-h-[110px] flex flex-col">
                  <div className="text-[10px] uppercase tracking-wider text-white/40 mb-2 flex items-center gap-1">{t.icon} {t.label}</div>
                  {eqItem ? (
                    <div className="flex-1 flex flex-col items-start gap-2">
                      <ItemPreview item={eqItem} size={44} />
                      <div className="text-xs text-white/80 truncate w-full">{eqItem.name}</div>
                      <button onClick={() => unequip(t.key)} className="text-[10px] text-red-300 hover:text-red-200" data-testid={`unequip-${t.key}`}>Gỡ trang bị</button>
                    </div>
                  ) : (
                    <div className="flex-1 grid place-items-center text-white/30 text-xs">Trống</div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Type tabs */}
        <div className="flex gap-2 overflow-x-auto pb-1" data-testid="shop-tabs">
          {TYPES.map((t) => (
            <button
              key={t.key}
              onClick={() => setFilter(t.key)}
              data-testid={`shop-tab-${t.key}`}
              className={`px-4 py-2 rounded-full text-xs whitespace-nowrap border transition ${filter === t.key ? "bg-amber-400/15 border-amber-400/50 text-amber-300" : "bg-white/[0.02] border-white/10 text-white/60 hover:text-white"}`}
            >
              <span className="mr-1">{t.icon}</span>{t.label}
            </button>
          ))}
        </div>

        {/* Items grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="items-grid">
          {filtered.length === 0 && <div className="col-span-full text-white/40 text-center py-10 text-sm">Chưa có vật phẩm loại này</div>}
          {filtered.map((item) => {
            const rs = RARITY_STYLES[item.rarity] || RARITY_STYLES.common;
            const owned = ownedIds.has(item.id);
            const equipped = equippedIds.has(item.id);
            return (
              <div key={item.id} className="rounded-2xl border p-4 flex flex-col" style={{ background: rs.bg, borderColor: rs.border }} data-testid={`item-${item.id}`}>
                <div className="flex items-start justify-between gap-2 mb-3">
                  <div>
                    <div className="text-[10px] uppercase tracking-widest font-bold mb-1" style={{ color: rs.color }}>{rs.label}</div>
                    <div className="font-display font-bold text-sm">{item.name}</div>
                  </div>
                  {equipped && <span className="text-[10px] px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 flex items-center gap-1"><Check size={10} /> Đang dùng</span>}
                </div>

                <div className="rounded-xl bg-[#05050a]/60 p-4 grid place-items-center min-h-[80px] mb-3">
                  <ItemPreview item={item} />
                </div>

                {item.description && <div className="text-xs text-white/50 mb-3 line-clamp-2">{item.description}</div>}

                {owned ? (
                  equipped ? (
                    <button disabled className="w-full px-3 py-2 rounded-md text-xs bg-white/5 border border-white/10 text-white/40">Đã trang bị</button>
                  ) : (
                    <button onClick={() => equip(item)} data-testid={`equip-${item.id}`} className="w-full px-3 py-2 rounded-md text-xs bg-cyan-400/15 border border-cyan-400/40 text-cyan-300 hover:bg-cyan-400/25">Trang bị</button>
                  )
                ) : (
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      disabled={!item.price_vnd || busy}
                      onClick={() => buy(item, "vnd")}
                      data-testid={`buy-vnd-${item.id}`}
                      className="px-2 py-2 rounded-md text-[11px] border disabled:opacity-30 disabled:cursor-not-allowed bg-amber-400/10 border-amber-400/30 text-amber-300 hover:bg-amber-400/20"
                    >
                      <div className="flex flex-col items-center">
                        <span className="text-[9px] opacity-70 leading-none">VND</span>
                        <span className="font-mono-num font-bold mt-0.5">{item.price_vnd ? formatVND(item.price_vnd) : "—"}</span>
                      </div>
                    </button>
                    <button
                      disabled={!item.price_points || busy}
                      onClick={() => buy(item, "points")}
                      data-testid={`buy-pts-${item.id}`}
                      className="px-2 py-2 rounded-md text-[11px] border disabled:opacity-30 disabled:cursor-not-allowed bg-cyan-400/10 border-cyan-400/30 text-cyan-300 hover:bg-cyan-400/20"
                    >
                      <div className="flex flex-col items-center">
                        <span className="text-[9px] opacity-70 leading-none">Điểm</span>
                        <span className="font-mono-num font-bold mt-0.5">{item.price_points || "—"}</span>
                      </div>
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
